// Theme follows prefers-color-scheme; a manual override lives in localStorage (per-viewer convenience).
// Storage can be blocked (private mode); that degrades to an in-memory choice with a console warning.
import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export const THEME_KEY = 'fl-theme';

let memoryChoice: ThemeChoice | null = null;
const listeners = new Set<() => void>();

export function getStoredTheme(): ThemeChoice {
  let v: string | null = null;
  try {
    v = localStorage.getItem(THEME_KEY);
  } catch (e) {
    console.warn(`Theme preference could not be read from storage: ${String(e)}`);
    return memoryChoice ?? 'system';
  }
  if (v === 'light' || v === 'dark') return v;
  return memoryChoice ?? 'system';
}

export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(choice: ThemeChoice) {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.dataset.theme = choice;
}

export function setTheme(choice: ThemeChoice) {
  memoryChoice = choice === 'system' ? null : choice;
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
  } catch (e) {
    console.warn(`Theme preference could not be saved (kept for this tab only): ${String(e)}`);
  }
  applyTheme(choice);
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener?.('change', l);
  return () => {
    listeners.delete(l);
    mq.removeEventListener?.('change', l);
  };
}

export function useTheme() {
  const choice = useSyncExternalStore(subscribe, getStoredTheme, () => 'system' as ThemeChoice);
  return { choice, resolved: resolveTheme(choice), setTheme };
}
